import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Icon } from "@iconify/react";
import { useState } from "react";
import CommonFreeTextInput from "../../common/inputs/CommonFreeTextInput";
import CommonTextAreaInput from "../../common/inputs/CommonTextAreaInput";
import CommonDropdownInput from "../../common/inputs/CommonDropdownInput";
import { pointsConfig } from "../../../pointsConfig";
import PointsCreateRewardWallet from "./PointsCreateRewardWallet";
import PointsCreateRewardSubscription from "./PointsCreateRewardSubscription";
import PointsCreateRewardAirtime from "./PointsCreateRewardAirtime";
import PointsCreateRewardBooking from "./PointsCreateRewardBooking";
import { enqueueSnackbar } from "notistack";
import { useMilesPoints } from "../../../hooks/useMilesPoints";

const style = {
  position: "absolute",
  top: "50%",
  right: "0%",
  transform: "translate(0%, -50%)",
  width: window.innerWidth > 600 ? "40vw" : "100vw",
  bgcolor: "background.paper",
  boxShadow: 24,
};

export default function PointsCreateRewardModal({ open, setOpen, callback }) {
  const { createReward } = useMilesPoints();
  const rewardTypeOptions = Object.values(
    pointsConfig.rewardCreation.rewardTypes
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    name: "",
    description: "",
    type: rewardTypeOptions[0].code,
    detail: {
      pointsCost: "",
      rewardValueInNaira: "",
      bookingDiscountType: pointsConfig.rewardCreation.bookingDiscountTypes.fixed
        .code,
      timeFrame: "30 days",
    },
  });

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (!data.name)
      return enqueueSnackbar("Title is required", { variant: "warning" });
    if (!data.description)
      return enqueueSnackbar("Description is required", { variant: "warning" });
    if (!data.detail.pointsCost)
      return enqueueSnackbar("Points Cost is required", { variant: "warning" });
    if (
      !data.detail.rewardValueInNaira &&
      data.type !== pointsConfig.rewardCreation.rewardTypes.subscription.code
    )
      return enqueueSnackbar("Amount is required", { variant: "warning" });
    if (
      data.type === pointsConfig.rewardCreation.rewardTypes.booking.code &&
      data.detail.bookingDiscountType ===
        pointsConfig.rewardCreation.bookingDiscountTypes.percentage.code
    ) {
      const pct = Number(data.detail.rewardValueInNaira);
      if (pct > 100)
        return enqueueSnackbar("Percentage discount cannot exceed 100", {
          variant: "warning",
        });
    }
    const payload = {
      title: data.name,
      description: data.description,
      type: data.type,
      pointsCost: Number(data.detail.pointsCost),
    };
    if (
      data.type === pointsConfig.rewardCreation.rewardTypes.subscription.code
    ) {
      payload.timeFrame = data.detail.timeFrame;
    } else {
      payload.rewardValueInNaira = Number(data.detail.rewardValueInNaira);
      if (
        data.type === pointsConfig.rewardCreation.rewardTypes.booking.code
      ) {
        payload.bookingDiscountType = data.detail.bookingDiscountType;
      }
    }
    await createReward({
      setLoading,
      payload,
      callback: (reward) => {
        callback?.(reward);
        enqueueSnackbar("Reward created successfully", { variant: "success" });
        setOpen(false);
      },
    });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div className="flex flex-col font-main h-screen max-w-[100vw]">
            <div className="w-full max-w-[100vw] bg-primary1 p-4 text-white font-semibold flex justify-between">
              <span>Create Reward</span>
              <button onClick={() => handleClose()}>
                <Icon
                  icon="material-symbols:close"
                  className={`text-secondary text-3xl`}
                />
              </button>
            </div>
            <div className="flex flex-1 flex-col overflow-y-scroll scroll-hide relative">
              <div className="flex w-full p-4 flex-col">
                <CommonFreeTextInput
                  label="Name"
                  placeholder="e.g Starter"
                  value={data.name}
                  onChange={(val) => setData({ ...data, name: val })}
                />
                <CommonTextAreaInput
                  label="Description"
                  className="mt-4"
                  value={data.description}
                  onChange={(val) => setData({ ...data, description: val })}
                />

                <hr className="w-full border border-gray-300 my-4" />

                <CommonDropdownInput
                  label="Type"
                  options={rewardTypeOptions}
                  optionObject
                  value={data.type}
                  onChange={(val) => setData({ ...data, type: val })}
                />

                <hr className="w-full border border-gray-300 my-4" />

                <div className="w-full flex flex-col">
                  {(() => {
                    switch (data.type) {
                      case pointsConfig.rewardCreation.rewardTypes.wallet.code:
                        return (
                          <PointsCreateRewardWallet
                            data={data.detail}
                            onChange={(detail) => setData({ ...data, detail })}
                          />
                        );
                      case pointsConfig.rewardCreation.rewardTypes.subscription
                        .code:
                        return (
                          <PointsCreateRewardSubscription
                            data={data.detail}
                            onChange={(detail) => setData({ ...data, detail })}
                          />
                        );
                      case pointsConfig.rewardCreation.rewardTypes.airtime.code:
                        return (
                          <PointsCreateRewardAirtime
                            data={data.detail}
                            onChange={(detail) => setData({ ...data, detail })}
                          />
                        );
                      case pointsConfig.rewardCreation.rewardTypes.booking.code:
                        return (
                          <PointsCreateRewardBooking
                            data={data.detail}
                            onChange={(detail) => setData({ ...data, detail })}
                          />
                        );

                      default:
                        return null;
                    }
                  })()}
                </div>
              </div>
              <div className="flex-1"></div>
              <div
                className="w-full flex p-2 lg:p-4 flex-shrink-0 sticky bottom-0 bg-white gap-3 items-center"
                style={{
                  boxShadow:
                    "0 -4px 6px -1px rgba(0,0,0,0.1), 0 -2px 4px -2px rgba(0,0,0,0.1)",
                }}
              >
                <button
                  className="h-12 flex items-center justify-center w-20"
                  onClick={() => handleClose()}
                >
                  Close
                </button>
                <button
                  className="flex flex-1 items-center justify-center h-12 rounded-md text-white text-sm font-bold bg-primary1"
                  onClick={() => handleConfirm()}
                >
                  {loading ? (
                    <div className="buttonLoader"></div>
                  ) : (
                    <>Confirm</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}
